import { PageContainer } from '@ant-design/pro-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Flex, Input, Select } from 'antd';
import { useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import HotRadarCardList from '../components/HotRadarCardList';
import HotRadarDetailDrawer from '../components/HotRadarDetailDrawer';
import {
  getHotRadar,
  queryHotRadarAccounts,
  queryHotRadars,
  removeHotRadar,
} from '../services/hot-radars';
import { formatTraceAccountName } from '../utils/platform';

const { Search } = Input;

const resourceName = '爆款拆解';

const platformFilterOptions = [
  { label: '全部平台', value: 'all' },
  { label: '抖音', value: 'douyin' },
  { label: '小红书', value: 'xhs' },
];

const getQueryValue = (query, key, defaultValue = '') => {
  const value = query[key];
  if (Array.isArray(value)) {
    return value.at(-1) || defaultValue;
  }
  return value || defaultValue;
};

const HotRadars = () => {
  const [detailRecord, setDetailRecord] = useState(null);
  const [query, setQuery, queryString] = useQueryParams();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();
  const platform = getQueryValue(query, 'platform', 'all');
  const accountId = getQueryValue(query, 'accountId');
  const contentTitle = getQueryValue(query, 'contentTitle');

  const reload = () =>
    queryClient.invalidateQueries({ queryKey: ['trace', 'hot-radars', organizationId] });

  const { data: listResult, isLoading: loading } = useQuery({
    queryKey: ['trace', 'hot-radars', organizationId, queryString],
    queryFn: () =>
      queryHotRadars({
        organizationId,
        accountId: accountId || undefined,
        contentTitle: contentTitle || undefined,
        platform: platform === 'all' ? undefined : platform,
        current: 1,
        pageSize: 60,
      }),
  });

  const { data: accountOptions = [], isLoading: loadingAccounts } = useQuery({
    enabled: Boolean(organizationId),
    queryKey: ['trace', 'hot-radars', 'accounts', organizationId],
    queryFn: () => queryHotRadarAccounts({ organizationId }),
    select: (accounts) => [
      { label: '全部账号', value: 'all' },
      ...accounts.map((account) => ({
        label: formatTraceAccountName(account),
        value: account.accountId,
      })),
    ],
  });

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: removeHotRadar,
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: getHotRadar,
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const updateSearchParam = (key, value) => {
    setQuery({ [key]: value || undefined });
  };

  return (
    <PageContainer content="拆解爆款内容，沉淀脚本结构与优化策略。">
      <Card variant="borderless">
        <Flex vertical gap="large">
          <Flex gap="middle" wrap>
            <Select
              value={platform}
              onChange={(value) =>
                updateSearchParam('platform', value === 'all' ? undefined : value)
              }
              options={platformFilterOptions}
              styles={{ root: { width: 120 } }}
              variant="filled"
            />
            <Select
              loading={loadingAccounts}
              options={accountOptions}
              value={accountId || 'all'}
              onChange={(value) =>
                updateSearchParam('accountId', value === 'all' ? undefined : value)
              }
              styles={{ root: { width: 260 } }}
              variant="filled"
            />
            <Search
              allowClear
              defaultValue={contentTitle}
              key={`title-${contentTitle || 'empty'}`}
              placeholder="爆款标题"
              onSearch={(value) => updateSearchParam('contentTitle', value)}
              styles={{ root: { flex: 1, minWidth: 260 } }}
              variant="filled"
            />
          </Flex>
          <HotRadarCardList
            dataSource={listResult?.data || []}
            loading={loading}
            removing={removing}
            onDetail={(record) => getRecord({ data: { id: record.id, organizationId } })}
            onRemove={(record) => removeRecord({ data: { id: record.id, organizationId } })}
          />
        </Flex>
      </Card>
      <HotRadarDetailDrawer
        record={detailRecord}
        open={Boolean(detailRecord)}
        onClose={() => setDetailRecord(null)}
      />
    </PageContainer>
  );
};

export default HotRadars;
